import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Eye, MousePointerClick, TrendingUp, ExternalLink } from "lucide-react";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";

interface PartnerPageAnalyticsProps {
  pageId: string;
}

type Period = 7 | 30 | 90;

export function PartnerPageAnalytics({ pageId }: PartnerPageAnalyticsProps) {
  const [period, setPeriod] = useState<Period>(30);
  const since = subDays(new Date(), period).toISOString();

  const { data: views = [] } = useQuery({
    queryKey: ["partner-analytics-views", pageId, period],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_page_views")
        .select("viewed_at")
        .eq("landing_page_id", pageId)
        .gte("viewed_at", since);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: clicks = [] } = useQuery({
    queryKey: ["partner-analytics-clicks", pageId, period],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_button_clicks")
        .select("clicked_at, button_url, button_text")
        .eq("landing_page_id", pageId)
        .gte("clicked_at", since);
      if (error) throw error;
      return data || [];
    },
  });

  const totalViews = views.length;
  const totalClicks = clicks.length;
  const conversionRate = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : "0";

  // Chart data: group by day
  const chartMap = new Map<string, { views: number; clicks: number }>();
  for (let i = 0; i < period; i++) {
    const day = format(subDays(new Date(), period - 1 - i), "yyyy-MM-dd");
    chartMap.set(day, { views: 0, clicks: 0 });
  }
  views.forEach((v) => {
    const day = format(new Date(v.viewed_at), "yyyy-MM-dd");
    const entry = chartMap.get(day);
    if (entry) entry.views++;
  });
  clicks.forEach((c) => {
    const day = format(new Date(c.clicked_at), "yyyy-MM-dd");
    const entry = chartMap.get(day);
    if (entry) entry.clicks++;
  });
  const chartData = Array.from(chartMap.entries()).map(([date, val]) => ({
    date: format(new Date(date), "dd/MM", { locale: ptBR }),
    Impressões: val.views,
    Cliques: val.clicks,
  }));

  // Top buttons
  const buttonMap = new Map<string, { text: string; url: string; count: number }>();
  clicks.forEach((c) => {
    const key = `${c.button_text}|${c.button_url}`;
    const existing = buttonMap.get(key);
    if (existing) {
      existing.count++;
    } else {
      buttonMap.set(key, { text: c.button_text, url: c.button_url, count: 1 });
    }
  });
  const topButtons = Array.from(buttonMap.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">📊 Analytics</CardTitle>
        <div className="flex gap-1">
          {([7, 30, 90] as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {p}d
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Metric cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
            <Eye className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{totalViews}</p>
              <p className="text-xs text-muted-foreground">Impressões</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
            <MousePointerClick className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{totalClicks}</p>
              <p className="text-xs text-muted-foreground">Cliques</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
            <TrendingUp className="h-5 w-5 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{conversionRate}%</p>
              <p className="text-xs text-muted-foreground">Conversão</p>
            </div>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={period > 30 ? 6 : "preserveStartEnd"} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Impressões" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Cliques" fill="hsl(142 71% 45%)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top buttons */}
        {topButtons.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">Botões mais clicados</p>
            <div className="space-y-2">
              {topButtons.map((btn, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-muted/30">
                  <div className="flex items-center gap-2 min-w-0">
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium">{btn.text}</span>
                  </div>
                  <span className="font-bold text-primary ml-2">{btn.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
